/**
 * India GST invoice with several lines at different rates (B-0133), and a small rate finder. Pure.
 * The single-amount maths (gst.ts: gstCalc, rupeesInWords) is NOT changed: each rate group is passed through gstCalc, so the
 * rounding rule is the same (CGST and SGST/UTGST are equal halves; in Remove GST mode an odd-paisa GST moves the base by 1 paisa).
 *
 * Rounding on an invoice: lines at the same rate are added first, then GST is worked out once on that rate's subtotal (the way an HSN or
 * rate-wise tax summary is laid out). An invoice that works GST out line by line can differ from this by a few paise.
 * There is no GST API endpoint, so nothing here is synced to the API.
 */
import type { CalcStep } from "@/lib/calc/steps";
import { gstCalc, rupeesInWords, type GstSupply, type GstResult } from "./gst";

export interface GstLine { description?: string; amount: number; ratePct: number }
export interface GstRateGroup extends GstResult { ratePct: number; lines: number }
export interface GstInvoice { groups: GstRateGroup[]; base: number; gst: number; cgst: number; sgst: number; utgst: number; igst: number; total: number; words: string }

const paise = (n: number) => Math.round((n + Number.EPSILON) * 100);

/** `inclusive` false: every amount is before GST. true: every amount already includes GST. */
export function gstInvoice(lines: GstLine[], inclusive: boolean, supply: GstSupply): GstInvoice {
  const by = new Map<number, { count: number; sumP: number }>();
  for (const l of lines) {
    const g = by.get(l.ratePct) ?? { count: 0, sumP: 0 };
    g.count += 1; g.sumP += paise(l.amount); by.set(l.ratePct, g);
  }
  const groups: GstRateGroup[] = Array.from(by.entries()).sort((a, b) => a[0] - b[0]).map(([ratePct, g]) => ({ ratePct, lines: g.count, ...gstCalc(g.sumP / 100, ratePct, inclusive, supply) }));
  // Sum in whole paise so the totals are exact.
  const sum = (k: keyof GstResult) => groups.reduce((a, g) => a + paise(g[k]), 0) / 100;
  const total = sum("total");
  return { groups, base: sum("base"), gst: sum("gst"), cgst: sum("cgst"), sgst: sum("sgst"), utgst: sum("utgst"), igst: sum("igst"), total, words: rupeesInWords(total) };
}

/** The working: one step per rate, then the invoice total. `f` formats rupees. */
export function gstInvoiceSteps(inv: GstInvoice, inclusive: boolean, supply: GstSupply, f: (n: number) => number | string = (n) => n): CalcStep[] {
  const steps: CalcStep[] = inv.groups.map((g) => ({
    label: `${g.ratePct}% lines (${g.lines})`,
    formula: inclusive ? "Amount incl. GST - GST; GST = amount - amount / (1 + rate); odd paisa moves the base by 1 paisa" : "GST = base x rate; each half (CGST and SGST/UTGST) = base x rate / 2, rounded",
    inputs: { [inclusive ? "amount incl. GST" : "base"]: f(inclusive ? g.total : g.base), rate: `${g.ratePct}%`, supply: supply === "inter" ? "IGST" : supply === "ut" ? "CGST + UTGST" : "CGST + SGST" },
    result: supply === "inter" ? `IGST ${f(g.igst)}, total ${f(g.total)}` : `CGST ${f(g.cgst)} + ${supply === "ut" ? "UTGST" : "SGST"} ${f(supply === "ut" ? g.utgst : g.sgst)}, total ${f(g.total)}`,
  }));
  steps.push({ label: "Invoice total", formula: "Sum of the rate groups (base + GST)", inputs: { base: f(inv.base), GST: f(inv.gst) }, result: f(inv.total) });
  return steps;
}

export interface GstRateEntry { name: string; rate: number | null; hsn?: string; note?: string; terms: string }
export const GST_RATE_FINDER_SOURCE = {
  text: "Recommendations of the 56th GST Council meeting (Press Information Bureau, PRID 2163555), effective 22 September 2025 for most goods and services.",
  url: "https://www.pib.gov.in/PressReleasePage.aspx?PRID=2163555&reg=48&lang=2",
  readOn: "2026-10-30",
  caveat: "A short list of the items the release names, read through a summarising fetch of the first 100,000 of 102,777 characters. It is NOT the full rate schedule. HSN is given only where the release gives it, at chapter or heading level: items inside a chapter can carry a different rate, and SAC codes for services are not listed. Confirm the rate and HSN/SAC on the CBIC rate notifications (cbic-gst.gov.in) before invoicing.",
};
const E = (name: string, rate: number | null, terms: string, hsn?: string, note?: string): GstRateEntry => ({ name, rate, terms, ...(hsn ? { hsn } : {}), ...(note ? { note } : {}) });
export const GST_RATE_FINDER: GstRateEntry[] = [
  E("UHT milk", 0, "uht milk ultra high temperature"),
  E("Prepackaged and labelled chena or paneer", 0, "paneer chena cottage cheese", "0406"),
  E("Indian breads: chapati, roti, paratha", 0, "chapati roti paratha bread"),
  E("Individual life insurance", 0, "life insurance individual policy"),
  E("Individual health insurance", 0, "health insurance individual policy mediclaim"),
  E("Condensed milk, butter, ghee, cheese", 5, "condensed milk butter ghee cheese dairy", "0405 / 0406"),
  E("Dried nuts and dried fruits", 5, "nuts dried fruits almonds cashew raisins dry fruit", "0801-0813"),
  E("Pasta", 5, "pasta macaroni noodles", "1902"),
  E("Corn flakes", 5, "corn flakes cereal breakfast", "1904"),
  E("Chocolates", 5, "chocolate chocolates cocoa", "1806"),
  E("Coffee and tea extracts, sauces", 5, "coffee tea extract sauce ketchup sauces", "2101 / 2103"),
  E("Namkeen and bhujia", 5, "namkeen bhujia snacks", "2106"),
  E("Hair oil, shampoo", 5, "hair oil shampoo", "3305"),
  E("Toothpaste", 5, "toothpaste dental", "3306"),
  E("Toilet soap", 5, "soap toilet soap bath soap", "3401"),
  E("Toothbrushes", 5, "toothbrush toothbrushes", "9603"),
  E("Tractors and agricultural machinery", 5, "tractor tractors agricultural machinery farm", "8432 / 8433 / 8701"),
  E("Bicycles", 5, "bicycle bicycles cycle", "8712"),
  E("Most medicines and drugs", 5, "medicine medicines drugs tablets pharma", undefined, "Reduced from 12%. Some life-saving drugs have their own rate: check the schedule."),
  E("Glucometers and test strips", 5, "glucometer glucose test strips diabetes"),
  E("Medical devices such as thermometers and analytical instruments", 5, "thermometer medical device instrument"),
  E("Hotel accommodation up to Rs 7,500 per unit per day", 5, "hotel room accommodation stay lodging", undefined, "Service. Above Rs 7,500 per unit per day a different rate applies: not in the short list."),
  E("Gym, salon, barber and yoga services", 5, "gym salon barber yoga haircut fitness spa", undefined, "Services."),
  E("Air-conditioning machines", 18, "air conditioner ac air conditioning", "8415", "Reduced from 28%."),
  E("Dishwashing machines", 18, "dishwasher dishwashing machine", "8422", "Reduced from 28%."),
  E("Televisions", 18, "television tv", "8528", "All sizes at 18%."),
  E("Small petrol, LPG or CNG cars and small diesel cars", 18, "car cars small car hatchback", "8703", "Reduced from 28%. Larger cars are 40%."),
  E("Motorcycles up to 350cc", 18, "motorcycle motorbike scooter bike", "8711", "Reduced from 28%. Above 350cc is 40%."),
  E("Buses, trucks, ambulances and three-wheelers", 18, "bus truck ambulance three wheeler auto rickshaw", undefined, "Reduced from 28%."),
  E("Cement", 18, "cement building material", "2523", "Reduced from 28%."),
  E("Auto parts", 18, "auto parts car parts vehicle spare parts"),
  E("Aerated and sugary beverages", 40, "soft drink cola aerated sugary beverage soda", "2202"),
  E("Caffeinated beverages", 40, "energy drink caffeinated beverage", "2202 99 90"),
  E("Motor cars and motorcycles above the small-car and 350cc thresholds, aircraft for personal use, yachts", 40, "luxury car large car big bike aircraft yacht"),
  E("Pan masala, gutkha, cigarettes, chewing tobacco, unmanufactured tobacco, bidi", null, "pan masala gutkha cigarette cigarettes tobacco bidi chewing", "2106 90 20 / 2401-2404", "The release says these keep their existing rates until the compensation cess loan and interest are discharged; the Union Finance Minister sets the transition date. No rate is suggested here: check CBIC."),
];

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
/** Matches the item words (any word of the query found in the name or search terms) or an HSN prefix typed as digits. Best matches first. */
export function findGstRate(query: string): GstRateEntry[] {
  const q = norm(query);
  if (!q) return [];
  const digits = q.replace(/\s/g, "");
  if (/^\d{2,8}$/.test(digits)) {
    return GST_RATE_FINDER.filter((e) => (e.hsn ?? "").split("/").some((tok) => {
      const code = tok.replace(/\s/g, "");
      const range = code.match(/^(\d{4})-(\d{4})$/);
      // Range entries such as 0801-0813 match any heading inside the range.
      if (range) return digits.length >= 4 && Number(digits.slice(0, 4)) >= Number(range[1]) && Number(digits.slice(0, 4)) <= Number(range[2]);
      const t = code.replace(/\D/g, "");
      // The typed digits are a prefix of the code (89 -> 8903), or a longer code that starts with a heading we list (84151010 -> 8415).
      return t.length > 0 && (t.startsWith(digits) || (t.length >= 4 && digits.startsWith(t)));
    }));
  }
  const words = q.split(" ").filter((w) => w.length > 1);
  const scored = GST_RATE_FINDER.map((e) => {
    const hay = norm(`${e.name} ${e.terms}`);
    const hayWords = new Set(hay.split(" "));
    let score = 0;
    for (const w of words) { if (hayWords.has(w)) score += 2; else if (hay.includes(w)) score += 1; }
    return { e, score };
  }).filter((x) => x.score > 0);
  return scored.sort((a, b) => b.score - a.score).map((x) => x.e);
}
